/**
 * The rating pop-up (the founder, 2026-10-30): after a visitor's first copy or download in a page
 * view, a small card at the bottom of the screen asks "How was this tool?" with five stars, from
 * 1 (Bad) to 5 (Very good). Optional in every way:
 *
 *   * NEVER IN THE WAY. It appears RATE_DELAY_MS after the copy or download has already happened
 *     and been counted, so it can neither block nor delay one. It is not a dialog: it takes no
 *     focus, traps nothing and covers only its own corner. The x button closes it, and so does
 *     Esc while focus is in it; a keyboard visitor who rates or closes it gets focus back where
 *     it was before they reached the card.
 *   * NEVER NAGS. At most once per page view. After a rating, not again for that tool for
 *     RATED_DAYS; after a close, not again on any tool for CLOSED_DAYS. Both are remembered in
 *     localStorage (RATE_STORE: a tool's slug and a time, nothing else); where storage is blocked,
 *     only the once per page view holds.
 *   * NEVER WHILE SOMEONE TALKS. While the live translator, live typing or live captions listen,
 *     or a recording runs or is paused (BUSY_SELECTOR), it waits until they stop.
 *   * THE FOUNDER'S SWITCH. Before it shows, it asks the Worker whether it is on (GET
 *     PROMPTS_ENDPOINT, `{"rate": true|false}`), within PROMPTS_TIMEOUT_MS. Anything but a clear
 *     `true` (an error, a slow answer, an old Worker's 404) means it does not show.
 *
 * WHERE. Tool pages only (RATED_PAGES, the body's `data-page`), never the stats page. It hears
 * copies and downloads from lib/usage.ts (`onResult`), where every tool already counts them:
 * `downloaded`, `copied`, `record_download`, `live_copy`, `live_save`, `pdf_text_download` and
 * `live_translate_download`; and, through `noteResult`, the few with no beacon of their own (a
 * transcript page's summary and captions, live captions' save, the live translator's Copy).
 * Only boot.js starts it; audio-out.js and burn.js are heard through
 * their own copy of usage.ts, whose listeners are the page's, not the module's.
 *
 * WHAT GOES. A star tap is one usage beacon, `rate` with the stars, `copy` or `download`, and the
 * page's path (lib/usage.ts RATINGS), through `countUse` and its rules. Every word is the page
 * language's (`t`), written with textContent only.
 */

import { t } from './i18n.ts';
import {
  countUse,
  MAX_STARS,
  onResult,
  RATE_EVENT,
  type RateAfter,
  type UsageFields,
} from './usage.ts';

/** The localStorage key: `{"rated": {slug: ms}, "closed": ms}`. */
export const RATE_STORE = 'tw-rate';
/** How long after the first copy or download the card appears. */
export const RATE_DELAY_MS = 1500;
/** After a rating, that tool is not asked again for this long. */
export const RATED_DAYS = 30;
/** After the card is closed, no tool asks again for this long. */
export const CLOSED_DAYS = 7;
/** The Worker's switch (deploy/cloudflare-worker/worker.js `prompts`). */
export const PROMPTS_ENDPOINT = '/e/prompts';
/** The longest the switch may take to answer before the card is given up for this page view. */
export const PROMPTS_TIMEOUT_MS = 2500;
/** How often a busy page (a microphone in use) is looked at again. */
export const BUSY_POLL_MS = 1000;
/** How long "Thanks for your rating." stays before the card goes. */
export const THANKS_MS = 2000;
/**
 * A microphone in use: live typing, the live translator and live captions mark themselves
 * `is-listening`, Hold to talk `is-holding`, and a recording shows its Stop button while it runs
 * or is paused (lib/mic-panel.ts).
 */
export const BUSY_SELECTOR =
  '.is-listening, .is-recording, .is-holding, [data-rec-stop]:not([hidden])';
/**
 * The body's `data-page` values of the pages with a tool. boot.ts passes the attribute as it is,
 * not its own page name, which reads the two pages with their own bundle (audio-out, burn) as
 * 'static'.
 */
export const RATED_PAGES: ReadonlySet<string> = new Set([
  'tool',
  'format',
  'transcript',
  'editor',
  'autosub',
  'mic',
  'text-pdf',
  'subtitle-translate',
  'live-translate',
  'calculator',
  'audio-out',
  'burn',
  // SEO_BRIEF_11 section 2.1: /video-compressor and /mp4-converter, with their own bundle too.
  'video-out',
]);

const DAY_MS = 86_400_000;
const TOOL = /^[a-z0-9-]{1,48}$/;

/** What this browser remembers: when each tool was rated, and when the card was last closed. */
export interface RateMemory {
  rated: Record<string, number>;
  closed: number;
}

/** The slice of Storage this uses. */
export interface RateStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const isTime = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;

/** The stored memory, an empty one when there is none or it is garbage, null when unreadable. */
export function readMemory(storage: RateStorage | null): RateMemory | null {
  if (storage === null) return null;
  let text: string | null;
  try {
    text = storage.getItem(RATE_STORE);
  } catch {
    return null;
  }
  const memory: RateMemory = { rated: {}, closed: 0 };
  if (text === null) return memory;
  try {
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed !== 'object' || parsed === null) return memory;
    const { rated, closed } = parsed as Record<string, unknown>;
    if (isTime(closed)) memory.closed = closed;
    if (typeof rated === 'object' && rated !== null) {
      for (const [tool, at] of Object.entries(rated)) {
        if (TOOL.test(tool) && isTime(at)) memory.rated[tool] = at;
      }
    }
  } catch {
    /* garbage reads as nothing remembered */
  }
  return memory;
}

/** True when `tool` may ask now: not closed in CLOSED_DAYS, not rated in RATED_DAYS. */
export function mayAsk(memory: RateMemory | null, tool: string, now: number): boolean {
  if (memory === null) return true;
  if (now - memory.closed < CLOSED_DAYS * DAY_MS) return false;
  const rated = memory.rated[tool];
  return rated === undefined || now - rated >= RATED_DAYS * DAY_MS;
}

/** Write `change` into the memory, dropping ratings older than RATED_DAYS. Never throws. */
function remember(storage: RateStorage | null, now: number, change: (m: RateMemory) => void): void {
  try {
    const memory = readMemory(storage);
    if (storage === null || memory === null) return;
    for (const [tool, at] of Object.entries(memory.rated)) {
      if (now - at >= RATED_DAYS * DAY_MS) delete memory.rated[tool];
    }
    change(memory);
    storage.setItem(RATE_STORE, JSON.stringify(memory));
  } catch {
    /* blocked storage: only the once per page view holds */
  }
}

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Whether the Worker has the pop-up on: true only for a 2xx `{"rate": true}` within `timeoutMs`.
 * Every failure (an error, a timeout, another status, another body) is false. Never throws.
 */
export async function askPromptSwitch(
  fetchFn: Fetch | undefined = globalThis.fetch?.bind(globalThis),
  timeoutMs: number = PROMPTS_TIMEOUT_MS,
): Promise<boolean> {
  if (typeof fetchFn !== 'function') return false;
  const abort = typeof AbortController === 'function' ? new AbortController() : null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<false>((resolve) => {
    timer = setTimeout(() => {
      abort?.abort();
      resolve(false);
    }, timeoutMs);
  });
  const asked = (async (): Promise<boolean> => {
    try {
      const res = await fetchFn(PROMPTS_ENDPOINT, {
        credentials: 'omit',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        ...(abort === null ? {} : { signal: abort.signal }),
      });
      if (!res.ok) return false;
      const body: unknown = await res.json();
      return (
        typeof body === 'object' && body !== null && (body as { rate?: unknown }).rate === true
      );
    } catch {
      return false;
    }
  })();
  try {
    return await Promise.race([asked, late]);
  } finally {
    clearTimeout(timer);
  }
}

/** What the pop-up reads and writes; the page's own by default, a stand-in in tests. */
export interface PromptHost {
  /** The body's `data-page` (boot.ts's page name). */
  page: string;
  /** `location.pathname`. */
  path: string;
  now: () => number;
  /** localStorage, or null when it cannot be reached. */
  storage: () => RateStorage | null;
  setTimeout: (fn: () => void, ms: number) => unknown;
  /** True while a microphone is in use (BUSY_SELECTOR). */
  busy: () => boolean;
  /** The Worker's switch (`askPromptSwitch`). */
  askSwitch: () => Promise<boolean>;
  document: Document;
  /** Send the rating (`countUse`). */
  send: (tool: string, fields: UsageFields['rate']) => void;
}

/** The pop-up for a test to look at. */
export interface RatingPrompt {
  /** The card while it is on the page, else null. */
  card(): HTMLElement | null;
  /** Stop listening (the card, if shown, stays until it is closed). */
  stop(): void;
}

function pageHost(page: string): PromptHost | null {
  if (typeof document === 'undefined' || typeof window === 'undefined') return null;
  if (typeof location === 'undefined') return null;
  return {
    page,
    path: location.pathname,
    now: () => Date.now(),
    storage: () => {
      try {
        return globalThis.localStorage ?? null;
      } catch {
        return null;
      }
    },
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    busy: () => document.querySelector(BUSY_SELECTOR) !== null,
    askSwitch: () => askPromptSwitch(),
    document,
    send: (tool, fields) => countUse(RATE_EVENT, tool, 'unknown', fields),
  };
}

/**
 * Listen for this page view's first copy or download, and offer the card after it. Returns null,
 * and does nothing, on a page without a tool or anywhere something goes wrong.
 */
export function startRatingPrompt(
  page: string,
  host: PromptHost | null = pageHost(page),
): RatingPrompt | null {
  try {
    if (host === null || !RATED_PAGES.has(host.page) || host.path.startsWith('/e/')) return null;
    let asked = false;
    let shown: HTMLElement | null = null;

    const offer = (tool: string, after: RateAfter): void => {
      if (asked || !mayAsk(readMemory(host.storage()), tool, host.now())) return;
      asked = true;
      host.setTimeout(() => void whenQuiet(tool, after), RATE_DELAY_MS);
    };

    const whenQuiet = async (tool: string, after: RateAfter): Promise<void> => {
      try {
        if (host.busy()) {
          host.setTimeout(() => void whenQuiet(tool, after), BUSY_POLL_MS);
          return;
        }
        if (!(await host.askSwitch())) return;
        // Another tab may have rated or closed it meanwhile; a microphone may have started.
        if (!mayAsk(readMemory(host.storage()), tool, host.now())) return;
        if (host.busy()) {
          host.setTimeout(() => void whenQuiet(tool, after), BUSY_POLL_MS);
          return;
        }
        show(tool, after);
      } catch {
        /* the pop-up never surfaces an error */
      }
    };

    const show = (tool: string, after: RateAfter): void => {
      const doc = host.document;
      const make = <K extends keyof HTMLElementTagNameMap>(
        tag: K,
        cls: string,
        text = '',
      ): HTMLElementTagNameMap[K] => {
        const node = doc.createElement(tag);
        node.className = cls;
        if (text) node.textContent = text;
        return node;
      };
      const card = make('section', 'rate-card');
      card.setAttribute('aria-labelledby', 'rate-card-title');
      const close = make('button', 'rate-card__close', '×');
      close.type = 'button';
      close.setAttribute('aria-label', t('Close'));
      const title = make('p', 'rate-card__title', t('How was this tool?'));
      title.id = 'rate-card-title';
      const stars = make('div', 'rate-card__stars');
      stars.setAttribute('role', 'group');
      stars.setAttribute('aria-labelledby', 'rate-card-title');
      const scale = make('div', 'rate-card__scale');
      scale.setAttribute('aria-hidden', 'true');
      scale.append(make('span', '', t('Bad')), make('span', '', t('Very good')));
      const thanks = make('p', 'rate-card__thanks');
      thanks.setAttribute('role', 'status');

      // Where focus was before it came into the card. A keyboard visitor who rates or closes it
      // goes back there: the star they pressed is hidden and the x goes with the card, and focus
      // left on either would drop to <body>, so the next Tab would start at the top of the page.
      let back: HTMLElement | null = null;
      card.addEventListener('focusin', (ev) => {
        const from = ev.relatedTarget as HTMLElement | null;
        if (from !== null && typeof from.focus === 'function' && !card.contains(from)) back = from;
      });
      /** Focus out of the card to where it came from, if it is in the card. False if it stays. */
      const focusBack = (): boolean => {
        if (!card.contains(doc.activeElement)) return true;
        if (back === null || !back.isConnected) return false;
        back.focus();
        return !card.contains(doc.activeElement);
      };
      const remove = (): void => {
        card.remove();
        if (shown === card) shown = null;
      };
      const dismiss = (): void => {
        remember(host.storage(), host.now(), (m) => {
          m.closed = host.now();
        });
        focusBack();
        remove();
      };
      // Esc is the card's only while focus is in it (the listener is on the card). Anywhere else
      // it belongs to what has focus, the header menu or the captions' full view, and a close
      // there would silence every tool for 7 days without the visitor choosing it.
      const onKey = (ev: KeyboardEvent): void => {
        if (ev.key !== 'Escape' || ev.defaultPrevented) return;
        dismiss();
      };
      let rated = false;
      for (let n = 1; n <= MAX_STARS; n++) {
        const star = make('button', 'rate-card__star', '★');
        star.type = 'button';
        star.setAttribute('aria-label', t('{n} out of 5').replace('{n}', String(n)));
        star.addEventListener('click', () => {
          if (rated) return;
          rated = true;
          try {
            host.send(tool, { stars: n, after, page: host.path });
          } catch {
            /* counting never surfaces */
          }
          remember(host.storage(), host.now(), (m) => {
            m.rated[tool] = host.now();
          });
          thanks.textContent = t('Thanks for your rating.');
          // Before the star is hidden under it. With nowhere to go back to, the thanks line.
          if (!focusBack()) {
            thanks.tabIndex = -1;
            thanks.focus();
          }
          title.hidden = true;
          stars.hidden = true;
          scale.hidden = true;
          close.hidden = true;
          card.removeEventListener('keydown', onKey);
          host.setTimeout(remove, THANKS_MS);
        });
        stars.append(star);
      }
      close.addEventListener('click', dismiss);
      card.append(close, title, stars, scale, thanks);
      card.addEventListener('keydown', onKey);
      doc.body.append(card);
      shown = card;
    };

    const stop = onResult(offer);
    return { card: () => shown, stop };
  } catch {
    return null;
  }
}
